import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import {
  PrismBanner,
  PrismMetricBlock,
  PrismTooltipHeader,
} from '@mylighthouse/prism-ember';
import { formatNumber, t } from 'ember-intl';
import { and, or } from 'ember-truth-helpers';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import formatOdRateValue from 'lumen/helpers/format-od-rate-value';
import formatOtbValue from 'lumen/helpers/format-otb-value';
import getNumberSignClass from 'lumen/helpers/get-number-sign-class';
import hasFeature from 'lumen/helpers/has-feature';
import isOdTileVisible from 'lumen/helpers/is-od-tile-visible';
import {
  OD_DEFAULT_DATE_FORMAT,
  OD_SELECTED_TILES_IDS,
} from 'lumen/utils/constants/overview-dashboard-constants';

interface HotelOverviewDashboardTooltipsTooltipDailyPatternsSignature {
  Args: {
    isDailyAggregation: unknown;
    selectedRefDaysAgo: unknown;
    tooltipData: unknown;
  };
}

export default class HotelOverviewDashboardTooltipsTooltipDailyPatterns extends Component<HotelOverviewDashboardTooltipsTooltipDailyPatternsSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare odAccess: Services['od-access'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCurrentPrice() {
    let result = this.intl.t('general.missing');

    // If price is not null or undefined
    // @ts-expect-error: Incorrect type
    if (!isEmpty(this.args.tooltipData?.price)) {
      // @ts-expect-error: Incorrect type
      result = this.hotelAndMarketService.formatRate(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.args.tooltipData?.price,
        true,
        true,
        { maximumFractionDigits: 0, minimumFractionDigits: 0 },
        false,
      );
    }

    // The end point returns non empty message string in case LOS/Sold out. The current price will be null in this case
    // @ts-expect-error: Incorrect type
    if (this.args.tooltipData?.message) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      result = this.intl.t(this.args.tooltipData?.message);
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMarketPickupValue() {
    let result = this.intl.t('general.missing');

    // If marketPickupValue is not null or undefined
    // @ts-expect-error: Incorrect type
    if (!isEmpty(this.args.tooltipData?.marketPickupValue)) {
      // @ts-expect-error: Incorrect type
      if (this.args.tooltipData?.marketPickupValue === 0) {
        result = this.intl.t('overview_dashboard.kpi.no_change');
      } else {
        result = this.intl.formatNumber(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.args.tooltipData?.marketPickupValue,
          {
            signDisplay: 'always',
            style: 'percent',
          },
        );
      }
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMyOtbPickupValue() {
    let result = this.intl.t('general.missing');

    // If myOtbPickupPercentageValue is not null or undefined
    // @ts-expect-error: Incorrect type
    if (!isEmpty(this.args.tooltipData?.myOtbPickupPercentageValue)) {
      // @ts-expect-error: Incorrect type
      if (this.args.tooltipData?.myOtbPickupPercentageValue === 0) {
        result = this.intl.t('overview_dashboard.kpi.no_change');
      } else {
        result = this.intl.formatNumber(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.args.tooltipData?.myOtbPickupPercentageValue,
          {
            signDisplay: 'always',
            style: 'percent',
          },
        );

        // @ts-expect-error: Incorrect type
        if (!isEmpty(this.args.tooltipData?.myOtbPickupRoomnightsValue)) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          result += ` (${this.intl.formatNumber(this.args.tooltipData?.myOtbPickupRoomnightsValue, { signDisplay: 'always' })})`;
        }
      }
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMyPricePosition() {
    let result = this.intl.t('general.missing');

    // If myPricePosition is not null or undefined
    // @ts-expect-error: Incorrect type
    if (!isEmpty(this.args.tooltipData?.myPricePosition)) {
      // @ts-expect-error: Incorrect type
      result = `${this.args.tooltipData?.myPricePosition}/${this.args.tooltipData?.numberOfCompetitors}`;
    }

    // The end point returns non empty message string in case LOS/Sold out. The price position will be null in this case
    // @ts-expect-error: Incorrect type
    if (this.args.tooltipData?.message) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      result = this.intl.t(this.args.tooltipData?.message);
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRentalPickupValue() {
    let result = this.intl.t('general.missing');

    // If marketPickupValue is not null or undefined
    // @ts-expect-error: Incorrect type
    if (!isEmpty(this.args.tooltipData?.rentalPickupValue)) {
      // @ts-expect-error: Incorrect type
      if (this.args.tooltipData?.rentalPickupValue === 0) {
        result = this.intl.t('overview_dashboard.kpi.no_change');
      } else {
        result = this.intl.formatNumber(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          this.args.tooltipData?.rentalPickupValue,
          {
            signDisplay: 'always',
            style: 'percent',
          },
        );
      }
    }

    return result;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get priceOpportunityMessage() {
    const priceOpportunityChange =
      // @ts-expect-error: Incorrect type
      this.args.tooltipData.opportunity > 0 ? 'high' : 'low';
    return this.intl.t(
      `market_insight.opportunities.rate.${priceOpportunityChange}_short`,
    );
  }

  <template>
    {{! @glint-nocheck }}
    <div
      class="default-tooltip default-tooltip--no-max-width default-table-tooltip od-tooltip"
    >
      <PrismTooltipHeader>
        {{#if @isDailyAggregation}}
          {{dayjsFormat
            @tooltipData.fromDateString
            format=this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG
          }}
        {{else}}
          {{t
            "general.week_of"
            date=(dayjsFormat
              @tooltipData.fromDateString format=OD_DEFAULT_DATE_FORMAT
            )
          }}
        {{/if}}
      </PrismTooltipHeader>
      {{#if (and @tooltipData.hasOpportunity @isDailyAggregation)}}
        <PrismBanner
          @bannerStyle="warning"
          @bannerTitle={{this.priceOpportunityMessage}}
        />
      {{/if}}
      {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.pricing_difference)}}
        <div class="tooltip-section grid auto-cols-fr grid-flow-col">
          <PrismMetricBlock>
            <:value>
              {{this.formattedCurrentPrice}}
            </:value>
            <:label>
              <span class="text-200--regular">{{t
                  "overview_dashboard.current_price"
                }}</span>
            </:label>
          </PrismMetricBlock>
          <PrismMetricBlock>
            <:value>
              {{formatOdRateValue @tooltipData.medianCompsetPrice}}
            </:value>
            <:label>
              <span class="text-200--regular">{{t
                  "rates.median_comp_rate"
                }}</span>
            </:label>
          </PrismMetricBlock>
          <PrismMetricBlock>
            <:value>
              {{this.formattedMyPricePosition}}
            </:value>
            <:label>
              <span class="text-200--regular">{{t
                  "overview_dashboard.price_position"
                }}</span>
            </:label>
          </PrismMetricBlock>
        </div>
      {{/if}}
      {{#if
        (or
          this.odAccess.showMyOtbDatapoints
          this.odAccess.showMarketOtbDatapoints
        )
      }}
        <table class="default-table">
          <tbody>
            <tr>
              <td class="text-200--bold default-table__compact-header">
              </td>
              <td class="text-200--bold default-table__compact-header">
                {{t "rates.OTB"}}
              </td>
              <td class="text-200--bold default-table__compact-header">
                {{t "revenue_insight.pickup"}}
              </td>
            </tr>
            {{#if this.odAccess.showMyOtbDatapoints}}
              <tr>
                <td>
                  {{t "general.my_hotel"}}
                </td>
                <td>
                  <span
                    class={{if
                      (hasFeature "otb.empty_placeholder")
                      "text-brand font-bold"
                    }}
                  >
                    {{formatOtbValue @tooltipData.myOtbValue style="percent"}}
                    ({{formatNumber @tooltipData.myOtbRoomnightsValue}})
                  </span>
                </td>
                <td>
                  <span
                    class="{{getNumberSignClass
                        @tooltipData.myOtbPickupPercentageValue
                      }}
                      {{if
                        (hasFeature 'otb.empty_placeholder')
                        'text-brand font-bold'
                      }}"
                  >{{this.formattedMyOtbPickupValue}}</span>
                  {{@selectedRefDaysAgo.label}}
                </td>
              </tr>
            {{/if}}
            {{#if this.odAccess.showMarketOtbDatapoints}}
              <tr>
                <td>
                  {{t "menu.market"}}
                </td>
                <td>
                  <span
                    class={{if
                      (hasFeature "otb.empty_placeholder")
                      "text-brand font-bold"
                    }}
                  >
                    {{formatOtbValue
                      @tooltipData.marketOtbValue
                      style="percent"
                    }}
                  </span>
                </td>
                <td>
                  <span
                    class="{{getNumberSignClass @tooltipData.marketPickupValue}}
                      {{if
                        (hasFeature 'otb.empty_placeholder')
                        'text-brand font-bold'
                      }}"
                  >{{this.formattedMarketPickupValue}}</span>
                  {{@selectedRefDaysAgo.label}}
                </td>
              </tr>
            {{/if}}
            {{#if (isOdTileVisible OD_SELECTED_TILES_IDS.short_term_rental)}}
              <tr>
                <td>
                  {{t "general.short_term_rental"}}
                </td>
                <td>
                  <span
                    class={{if
                      (hasFeature "otb.empty_placeholder")
                      "text-brand font-bold"
                    }}
                  >
                    {{formatOtbValue
                      @tooltipData.rentalOtbValue
                      style="percent"
                    }}
                  </span>
                </td>
                <td>
                  <span
                    class="{{getNumberSignClass @tooltipData.rentalPickupValue}}
                      {{if
                        (hasFeature 'otb.empty_placeholder')
                        'text-brand font-bold'
                      }}"
                  >
                    {{this.formattedRentalPickupValue}}
                  </span>
                  {{@selectedRefDaysAgo.label}}
                </td>
              </tr>
            {{/if}}
          </tbody>
        </table>
      {{/if}}
    </div>
  </template>
}
